import { useEffect, useState, useCallback } from "react";
import { Search, BadgeCheck, MapPin, Star, Megaphone } from "lucide-react";
import AppShell from "@/components/AppShell";
import LeadDetailModal from "@/components/LeadDetailModal";
import { api } from "@/lib/api";
import { ORG_TYPES, orgLabel } from "@/lib/constants";

export default function Directory() {
  const [profiles, setProfiles] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchDir = useCallback((query, orgType) => {
    setLoading(true);
    api.get("/profiles/directory", { params: { q: query, org_type: orgType } })
      .then(({ data }) => setProfiles(data))
      .finally(() => setLoading(false));
  }, []);

  // Debounced search: refetch on query/type change.
  useEffect(() => {
    const id = setTimeout(() => fetchDir(q, type), 300);
    return () => clearTimeout(id);
  }, [q, type, fetchDir]);

  return (
    <AppShell title="Business Directory" subtitle="Discover verified businesses, partners and opportunities.">
      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); fetchDir(q, type); }} className="flex-1 min-w-[240px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="directory-search-input"
            placeholder="Search businesses, industries…"
            className="w-full h-11 pl-10 pr-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none" />
        </form>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="directory-type-filter"
          className="h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] outline-none">
          <option value="">All types</option>
          {ORG_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-44 rounded-xl border border-slate-200 bg-white animate-pulse" />)}
        </div>
      ) : profiles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="directory-empty">
          No businesses found. Try a different search.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <div key={p.user_id} role="button" tabIndex={0}
              onClick={() => setSelected({ userId: p.user_id })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected({ userId: p.user_id }); } }}
              className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-[#2563EB]/40 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-testid={`directory-card-${p.user_id}`}>
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-lg bg-[#0A0F1C] flex items-center justify-center font-heading font-bold text-white">
                  {p.org_name?.[0]?.toUpperCase() || "B"}
                </div>
                {p.featured && (p.featured_type === "sponsored_listing"
                  ? <span data-testid={`directory-sponsored-badge-${p.user_id}`} className="flex items-center gap-1 text-[10px] font-semibold text-white bg-[#6D28D9] px-1.5 py-0.5 rounded-full uppercase tracking-wide"><Megaphone size={11} /> Sponsored</span>
                  : <span data-testid={`directory-featured-badge-${p.user_id}`} className="flex items-center gap-1 text-[11px] font-semibold text-[#C2410C]"><Star size={12} className="fill-[#C2410C]" /> Featured</span>)}
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <h3 className="font-heading font-bold text-[#0A0F1C] truncate">{p.org_name}</h3>
                {p.verified && <BadgeCheck size={16} className="text-[#2563EB] shrink-0" />}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                <span className="capitalize">{orgLabel(p.org_type)}</span>
                {p.location && <><span>·</span><MapPin size={11} /> {p.location}</>}
              </div>
              <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-slate-400">{p.industry}</span>
                <span className="font-mono text-xs text-slate-500">Trust {p.trust_score}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <LeadDetailModal open={!!selected} onClose={() => setSelected(null)} userId={selected?.userId} />
    </AppShell>
  );
}
